import { isNil } from 'lodash';
import { CSSProperties, useId } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { getAnchorName, grid } from '@shared/styles';
import { FileUpload } from '@shared/types/fieldValues';
import { useImageSrcAndDimensions } from '@shared/util/hooks';
import styles from './ImageItem.module.css';

interface ImageItemProps {
  image: FileUpload;
  editable: boolean;
  onPress?: VoidFunction;
  onClear?: VoidFunction;
  maxHeight?: number;
  maxWidth?: number;
  style?: CSSProperties;
}

export function ImageItem({
  image,
  editable,
  onPress,
  onClear,
  maxWidth = window.innerWidth,
  maxHeight = window.innerHeight,
  style,
}: ImageItemProps) {
  const id = useId();
  const viewTransitionName = getAnchorName('image-item', id);

  const { src, width, height, ratio, resolved } = useImageSrcAndDimensions({
    image,
    containerWidth: maxWidth,
    containerHeight: maxHeight,
    size: 'contain',
  });

  if (!width || !height) {
    return (
      <View center style={{ width: maxHeight, height: maxHeight, viewTransitionName }}>
        <LoadingIndicator fill />
      </View>
    );
  }

  return (
    <View onClick={onPress} className={styles.imageItem} style={{ viewTransitionName }}>
      <AnchoredTooltip tooltip={image.name} relative>
        <View
          className={styles.imageContainer}
          style={{ width: maxHeight * ratio, maxWidth, height: maxHeight, ...style }}
        >
          <img src={src} alt={image.name} className={styles.image} />
          {!resolved && (
            <View absolute center className={styles.loadingIndicator}>
              <LoadingIndicator />
            </View>
          )}
        </View>
        {editable && !isNil(onClear) && (
          <View center stopPropagation className={styles.clearButton}>
            <Button size="small" icon="exit" iconOnly iconSize={grid(2)} onClick={onClear} />
          </View>
        )}
      </AnchoredTooltip>
    </View>
  );
}
